<form [formGroup]="contentSettingsForm" id="contentSettingsWrapper" (ngSubmit)="saveSettings()">

  <div class="backButton" (click)="builderService.navigateBack()">
    <img src="/assets/icons/arrow-left.svg" [alt]="'Go back' | translate" />
  </div>

  <div class="header-section">
    <div>
      <p class="title">{{ 'Content & Experience' | translate }}</p>
      <p class="subtitle">{{ 'Define how users interact with your app\'s content and how to reach you.' | translate }}</p>
    </div>

    <button type="submit" class="saveButton" [class.loading]="settingsProgress" [disabled]="settingsProgress">
      {{ 'Save Changes' | translate }}
    </button>
  </div>

  <!-- ── WEBVIEW MODE ONLY ── -->
  <div class="contentContainer" *ngIf="builderService.projectAccess === 'webview'">
    <div class="section-card webview-only-card" style="padding-bottom: 24px;">
      <div class="card-header">
        <h3>{{ 'Webview Config' | translate }}</h3>
      </div>
      <div class="card-body">
        <div class="fields-grid">
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'Website Link' | translate }}</label>
            </div>
            <div class="genie-input">
              <input type="text" placeholder="https://google.com" formControlName="mobile_webview_link" />
            </div>
            <p class="hint-text">{{ 'This option defines the home page for your webview app.' | translate }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ── STANDARD MODE ── -->
  <div class="contentContainer" *ngIf="builderService.projectAccess !== 'webview'">

    <!-- Row 1: Content card + Webview Config card -->
    <div class="top-row-grid">

      <!-- Content Card -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ 'Content' | translate }}</h3>
        </div>
        <div class="card-body">
          <div class="fields-grid">

            <!-- Category Sub-page Behavior -->
            <div class="field-wrapper">
              <div class="field-label-row">
                <label>{{ 'Category Sub-page Behavior' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="category_sub_page">
                  <option value="1">{{ 'Standard Grid' | translate }}</option>
                  <option value="null">{{ 'Disabled' | translate }}</option>
                </select>
              </div>
            </div>

            <!-- Default Landing Page -->
            <div class="field-wrapper">
              <div class="field-label-row">
                <label>{{ 'Default Landing Page' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="landing_page">
                  <option value="/">{{ 'Home Screen' | translate }}</option>
                  <option value="login">{{ 'login' | translate }}</option>
                  <option value="allProductsCategories">{{ 'All Products Categories' | translate }}</option>
                  <option value="products">{{ 'Products' | translate }}</option>
                  <option value="productsCategories">{{ 'Product Category' | translate }}</option>
                  <option value="productsSubCategories">{{ 'Product Sub Category' | translate }}</option>
                  <option value="account">{{ 'Account Page' | translate }}</option>
                  <option value="contact">{{ 'Contact Us' | translate }}</option>
                  <option value="about">{{ 'About Us' | translate }}</option>
                  <option value="orders">{{ 'Orders' | translate }}</option>
                  <option value="cart">{{ 'Cart' | translate }}</option>
                  <option value="terms">{{ 'Terms' | translate }}</option>
                </select>
              </div>
              <p class="hint-text">{{ 'This setting determines which page users will land on when they launch the app.' | translate }}</p>
            </div>

            <!-- Conditional: Select Product -->
            <div class="field-wrapper" *ngIf="isProductsLandingPageSelected()">
              <div class="field-label-row">
                <label>{{ 'Select Product' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="selected_product">
                  <option *ngFor="let product of standaloneProductsService.storedStandaloneProducts" [value]="product.Id || product.product_id">
                    {{ product.productName }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Conditional: Select Product Category -->
            <div class="field-wrapper" *ngIf="isCategoriesLandingPageSelected()">
              <div class="field-label-row">
                <label>{{ 'Select Product Category' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="product_category">
                  <option *ngFor="let cat of standaloneCategoriesService.storedStandaloneCategories" [value]="cat.id">
                    {{ cat.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Conditional: Select Product Sub-Category -->
            <div class="field-wrapper" *ngIf="isSubCategoriesLandingPageSelected()">
              <div class="field-label-row">
                <label>{{ 'Select Product Sub-Category' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="product_sub_category">
                  <option *ngFor="let subCat of selectedSubCategories" [value]="subCat.subCategoryId">
                    {{ subCat.subcategory }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Guest User Permission (toggle) -->
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ 'Guest User Permission' | translate }}</span>
                <span class="toggle-desc">{{ 'Allow browsing the app without logging in' | translate }}</span>
              </div>
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  [checked]="contentSettingsForm.get('disable_guest')?.value === '1'"
                  (change)="contentSettingsForm.patchValue({ disable_guest: $any($event.target).checked ? '1' : 'disabled' })"
                />
                <span class="toggle-slider"></span>
              </label>
            </div>

            <!-- Webview Checkout (if not standalone) -->
            <div class="field-wrapper" *ngIf="!builderService.standalone">
              <div class="field-label-row">
                <label>{{ 'Webview Checkout' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="enable_website_checkout">
                  <option value="disabled">{{ 'Disabled' | translate }}</option>
                  <option value="1">{{ 'Enabled' | translate }}</option>
                </select>
              </div>
              <p class="hint-text">{{ 'Disable the Native Checkout of the Application and enable the website Version, Usually used if you have modifications for the checkout page and you would like to represent it as it is ' | translate }}</p>
            </div>

          </div>
        </div>
      </div>

      <!-- Webview Config Card -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ 'Webview Config' | translate }}</h3>
        </div>
        <div class="card-body">
          <div class="fields-grid">

            <!-- Webview Link -->
            <div class="field-wrapper">
              <div class="field-label-row">
                <label>{{ 'Webview Link' | translate }}</label>
              </div>
              <div class="genie-input">
                <input type="text" formControlName="mobile_webview_link" placeholder="https://stacks.com/docs" />
              </div>
            </div>

            <!-- Browser Type -->
            <div class="field-wrapper">
              <div class="field-label-row">
                <label>{{ 'Browser Type' | translate }}</label>
              </div>
              <div class="genie-input">
                <select formControlName="browser_type">
                  <option value="appBrowser">{{ 'Internal in app browser' | translate }}</option>
                  <option value="mobileBrowser">{{ 'Default Mobile Device Browser Like Chrome' | translate }}</option>
                </select>
              </div>
            </div>

            <!-- Search Results Posts (WordPress, standalone only) -->
            <div class="field-wrapper" *ngIf="builderService.standalone">
              <div class="field-label-row">
                <label>{{ 'Search Results Posts (WordPress)' | translate }}</label>
              </div>
              <div class="genie-input">
                <input type="text" placeholder="https://mywordpressite.com" formControlName="search_results_posts" />
              </div>
              <p class="hint-text">{{ 'Enter your WordPress site URL to enable post search. Requires the Stacks plugin to be activated.' | translate }}</p>
            </div>

          </div>
        </div>
      </div>

    </div><!-- /top-row-grid -->

    <!-- Row 2: Support & Social (full width, 3-col) -->
    <div class="section-card">
      <div class="card-header">
        <h3>{{ 'Support & Social' | translate }}</h3>
      </div>
      <div class="card-body">
        <div class="fields-grid-3col">

          <!-- Contact Email -->
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'Contact Email' | translate }}</label>
            </div>
            <div class="genie-input icon-input">
              <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H13M1 1V9.5C1 10.0523 1.44772 10.5 2 10.5H12C12.5523 10.5 13 10.0523 13 9.5V1M1 1L7 6.5L13 1" stroke="#ADADAD" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input type="email" [placeholder]="'aaa@co.stacks' | translate" formControlName="contact_email" />
            </div>
          </div>

          <!-- Contact Phone -->
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'Contact Phone' | translate }}</label>
            </div>
            <div class="genie-input icon-input">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.60938 1H2C1.44772 1 1 1.44772 1 2V3.5C1 7.91828 4.58172 11.5 9 11.5H10.5C11.0523 11.5 11.5 11.0523 11.5 10.5V7.89063C11.5 7.68062 11.3741 7.49001 11.1797 7.40234L8.88281 6.39453C8.63672 6.28516 8.34961 6.35547 8.18164 6.5625L7.34375 7.625C6.16016 7.03516 5.21484 6.08984 4.625 4.90625L5.6875 4.06836C5.89453 3.90039 5.96484 3.61328 5.85547 3.36719L4.84766 1.07031C4.75977 0.875977 4.56836 0.75 4.35938 0.75L4.60938 1Z" stroke="#ADADAD" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input type="tel" placeholder="+52523652332" formControlName="contact_phone" />
            </div>
          </div>

          <!-- Instagram Handle -->
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'Instagram Handle' | translate }}</label>
            </div>
            <div class="genie-input icon-input">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="12" height="12" rx="3.5" stroke="#ADADAD" stroke-width="1"/>
                <circle cx="7" cy="7" r="2.5" stroke="#ADADAD" stroke-width="1"/>
                <circle cx="10.5" cy="3.5" r="0.5" fill="#ADADAD"/>
              </svg>
              <input type="text" [placeholder]="'@stacks_io' | translate" formControlName="contact_instagram" />
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Row 3: WordPress Integration (full width, 2-col) -->
    <div class="section-card">
      <div class="card-header">
        <h3>{{ 'WordPress Integration' | translate }}</h3>
      </div>
      <div class="card-body">
        <div class="fields-grid-2col">

          <!-- Sync Token -->
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'Sync with WordPress' | translate }}</label>
            </div>
            <div class="genie-input token-input">
              <input type="text" [value]="builderService.getToken()" readonly />
              <span *ngIf="tokenCopied" class="copied-label">{{ 'Copied!' | translate }}</span>
              <button type="button" class="copy-btn" (click)="copyToken()" title="{{ 'Copy token' | translate }}">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4.5" y="4.5" width="7.5" height="7.5" rx="1.5" stroke="currentColor" stroke-width="1"/>
                  <path d="M4.5 8.5H3C2.44772 8.5 2 8.05228 2 7.5V2C2 1.44772 2.44772 1 3 1H8.5C9.05228 1 9.5 1.44772 9.5 2V4.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <p class="hint-text">{{ 'Use the token above to sync your app with your xw site' | translate }}</p>
          </div>

          <!-- WordPress Website URL -->
          <div class="field-wrapper">
            <div class="field-label-row">
              <label>{{ 'WordPress Website URL' | translate }}</label>
            </div>
            <div class="genie-input">
              <input type="text" placeholder="https://mywordpressite.com" formControlName="wordpress_site_url" />
            </div>
          </div>

        </div>
      </div>
    </div>

  </div><!-- /contentContainer standard -->

</form>
